import { Injectable } from '@angular/core';
import {
  FolderNode,
  FolderTemplateNodeType,
} from '../models/folder-node.models';

@Injectable({
  providedIn: 'root',
})
export class FolderTreeService {
  generateId(): string {
    return `folder-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  calculateLevels(nodes: FolderNode[]): FolderNode[] {
    return nodes.map(node => ({
      ...node,
      level: this.calculateNodeLevel(node.id, nodes),
      hasChildren: this.hasChildren(node.id, nodes),
    }));
  }

  hasChildren(nodeId: string, nodes: FolderNode[]): boolean {
    return nodes.some(n => n.pid === nodeId);
  }

  getDescendantIds(nodeId: string, nodes: FolderNode[]): string[] {
    const children = nodes.filter(n => n.pid === nodeId);
    let descendants = children.map(c => c.id);

    for (const child of children) {
      descendants = descendants.concat(this.getDescendantIds(child.id, nodes));
    }

    return descendants;
  }

  getNodeWithAllDescendants(nodeId: string, nodes: FolderNode[]): FolderNode[] {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return [];

    const result: FolderNode[] = [node];
    const allDescendantIds = this.getDescendantIds(nodeId, nodes);
    const descendantNodes = nodes.filter(n => allDescendantIds.includes(n.id));

    result.push(...descendantNodes);
    return result;
  }

  isNodeVisible(node: FolderNode, nodes: FolderNode[]): boolean {
    if (!node.pid) {
      return true;
    }

    const parent = nodes.find(n => n.id === node.pid);
    if (!parent) {
      return true;
    }

    if (!parent.isExpanded) {
      return false;
    }

    return this.isNodeVisible(parent, nodes);
  }

  getVisibleNodes(nodes: FolderNode[]): FolderNode[] {
    return nodes.filter(node => this.isNodeVisible(node, nodes));
  }

  wouldCreateCycle(
    draggedNodeId: string,
    targetNodeId: string,
    nodes: FolderNode[]
  ): boolean {
    if (draggedNodeId === targetNodeId) {
      return true;
    }

    const descendants = this.getDescendantIds(draggedNodeId, nodes);
    return descendants.includes(targetNodeId);
  }

  isDescendantOf(
    nodeId: string,
    ancestorId: string,
    nodes: FolderNode[]
  ): boolean {
    const node = nodes.find(n => n.id === nodeId);
    if (!node || !node.pid) {
      return false;
    }

    if (node.pid === ancestorId) {
      return true;
    }

    return this.isDescendantOf(node.pid, ancestorId, nodes);
  }

  sortFoldersByName(nodes: FolderNode[]): FolderNode[] {
    return this.sortFoldersRecursively(null, nodes);
  }

  addRootFolder(nodes: FolderNode[]): {
    nodes: FolderNode[];
    newNodeId: string;
  } {
    const newFolder: FolderNode = {
      id: this.generateId(),
      name: 'New Folder',
      pid: null,
      isExpanded: true,
      isEditing: true,
      tp: FolderTemplateNodeType.Folder,
    };

    const updatedNodes = [...nodes, newFolder];
    return {
      nodes: this.calculateLevels(updatedNodes),
      newNodeId: newFolder.id,
    };
  }

  addDocumentPlaceholder(
    parentId: string,
    nodes: FolderNode[]
  ): { nodes: FolderNode[]; newNodeId: string } {
    const newDocumentPlaceholder: FolderNode = {
      id: this.generateId(),
      name: 'New Document Placeholder',
      pid: parentId,
      isExpanded: false,
      isEditing: true,
      tp: FolderTemplateNodeType.UploadRequest,
    };

    const parentIndex = nodes.findIndex(n => n.id === parentId);
    const newNodes = [...nodes];

    if (parentIndex !== -1) {
      newNodes[parentIndex] = { ...newNodes[parentIndex], isExpanded: true };
    }

    newNodes.splice(parentIndex + 1, 0, newDocumentPlaceholder);

    return {
      nodes: this.calculateLevels(newNodes),
      newNodeId: newDocumentPlaceholder.id,
    };
  }

  addRootDocumentPlaceholder(nodes: FolderNode[]): {
    nodes: FolderNode[];
    newNodeId: string;
  } {
    const newDocumentPlaceholder: FolderNode = {
      id: this.generateId(),
      name: 'New Document Placeholder',
      pid: null,
      isExpanded: false,
      isEditing: true,
      tp: FolderTemplateNodeType.UploadRequest,
    };

    const updatedNodes = [...nodes, newDocumentPlaceholder];
    return {
      nodes: this.calculateLevels(updatedNodes),
      newNodeId: newDocumentPlaceholder.id,
    };
  }

  addSubFolder(
    parentId: string,
    nodes: FolderNode[]
  ): { nodes: FolderNode[]; newNodeId: string } {
    const newFolder: FolderNode = {
      id: this.generateId(),
      name: 'New Folder',
      pid: parentId,
      isExpanded: true,
      isEditing: true,
      tp: FolderTemplateNodeType.Folder,
    };

    const parentIndex = nodes.findIndex(n => n.id === parentId);
    const newNodes = [...nodes];

    if (parentIndex !== -1) {
      newNodes[parentIndex] = { ...newNodes[parentIndex], isExpanded: true };
    }

    newNodes.splice(parentIndex + 1, 0, newFolder);

    return {
      nodes: this.calculateLevels(newNodes),
      newNodeId: newFolder.id,
    };
  }

  deleteFolder(nodeId: string, nodes: FolderNode[]): FolderNode[] {
    const nodesToRemove = [nodeId, ...this.getDescendantIds(nodeId, nodes)];
    const filteredNodes = nodes.filter(n => !nodesToRemove.includes(n.id));
    return this.calculateLevels(filteredNodes);
  }

  toggleExpanded(nodeId: string, nodes: FolderNode[]): FolderNode[] {
    return nodes.map(n =>
      n.id === nodeId ? { ...n, isExpanded: !n.isExpanded } : n
    );
  }

  moveNodeWithChildren(
    nodeId: string,
    fromIndex: number,
    toIndex: number,
    nodes: FolderNode[]
  ): FolderNode[] {
    const visibleNodes = this.getVisibleNodes(nodes);
    const targetVisibleNode = visibleNodes[toIndex];

    const nodeWithChildren = this.getNodeWithAllDescendants(nodeId, nodes);
    const nodeIds = nodeWithChildren.map(n => n.id);

    const nodesToMove = nodes.filter(n => nodeIds.includes(n.id));
    const remainingNodes = nodes.filter(n => !nodeIds.includes(n.id));

    let insertIndex: number;

    if (toIndex >= visibleNodes.length) {
      insertIndex = remainingNodes.length;
    } else if (targetVisibleNode) {
      const targetNodeIndex = remainingNodes.findIndex(
        n => n.id === targetVisibleNode.id
      );
      if (targetNodeIndex !== -1) {
        insertIndex =
          fromIndex < toIndex ? targetNodeIndex + 1 : targetNodeIndex;
      } else {
        insertIndex = remainingNodes.length;
      }
    } else {
      insertIndex = 0;
    }

    remainingNodes.splice(insertIndex, 0, ...nodesToMove);
    return remainingNodes;
  }

  moveNodeToNewParent(
    nodeId: string,
    newParentId: string | null,
    nodes: FolderNode[]
  ): FolderNode[] {
    const node = nodes.find(n => n.id === nodeId);
    if (!node) return nodes;

    const nodeWithChildren = this.getNodeWithAllDescendants(nodeId, nodes);
    const nodeIds = nodeWithChildren.map(n => n.id);

    const nodesToMove = nodes.filter(n => nodeIds.includes(n.id));
    const remainingNodes = nodes.filter(n => !nodeIds.includes(n.id));

    const updatedNodesToMove = nodesToMove.map(n =>
      n.id === nodeId ? { ...n, pid: newParentId } : n
    );

    let insertIndex = remainingNodes.length;

    if (newParentId) {
      const targetParentIndex = remainingNodes.findIndex(
        n => n.id === newParentId
      );
      if (targetParentIndex !== -1) {
        remainingNodes[targetParentIndex] = {
          ...remainingNodes[targetParentIndex],
          isExpanded: true,
        };
        insertIndex = targetParentIndex + 1;

        for (let i = targetParentIndex + 1; i < remainingNodes.length; i++) {
          const currentNode = remainingNodes[i];
          if (
            this.isDescendantOf(currentNode.id, newParentId, remainingNodes)
          ) {
            insertIndex = i + 1;
          } else {
            break;
          }
        }
      }
    }

    remainingNodes.splice(insertIndex, 0, ...updatedNodesToMove);
    return remainingNodes;
  }

  rebuildFolderTree(nodes: FolderNode[]): FolderNode[] {
    const sortedNodes = this.sortFoldersByName(nodes);
    return this.calculateLevels(sortedNodes);
  }

  getFolderIcon(node: FolderNode): string {
    if (node.tp === FolderTemplateNodeType.UploadRequest) {
      return '';
    }

    if (!node.hasChildren) {
      return 'folder';
    }
    return node.isExpanded ? 'folder_open' : 'folder';
  }

  getExpandIcon(node: FolderNode): string {
    if (node.tp === FolderTemplateNodeType.UploadRequest) {
      return '';
    }

    if (!node.hasChildren) {
      return 'chevron_right';
    }
    return node.isExpanded ? 'expand_more' : 'chevron_right';
  }

  isDocumentPlaceholder(node: FolderNode): boolean {
    return node.tp === FolderTemplateNodeType.UploadRequest;
  }

  initializeStructure(initialStructure: FolderNode[]): FolderNode[] {
    const nodes = [...initialStructure];
    const sortedNodes = this.sortFoldersByName(nodes);
    return this.calculateLevels(sortedNodes);
  }

  generateDropTargetIds(nodes: FolderNode[]): string[] {
    return ['main-list', ...nodes.map(n => n.id)];
  }

  private calculateNodeLevel(nodeId: string, nodes: FolderNode[]): number {
    const node = nodes.find(n => n.id === nodeId);
    if (!node || !node.pid) {
      return 0;
    }

    const parent = nodes.find(n => n.id === node.pid);
    if (!parent) {
      return 0;
    }

    return this.calculateNodeLevel(parent.id, nodes) + 1;
  }

  private sortFoldersRecursively(
    parentId: string | null,
    nodes: FolderNode[]
  ): FolderNode[] {
    const childNodes = nodes
      .filter(node => node.pid === parentId)
      .sort((a, b) => {
        if (a.tp !== b.tp) {
          return b.tp.localeCompare(a.tp);
        }

        return a.name.localeCompare(b.name, undefined, {
          numeric: true,
          sensitivity: 'base',
        });
      });

    const result: FolderNode[] = [];

    for (const childNode of childNodes) {
      result.push(childNode);

      const sortedChildren = this.sortFoldersRecursively(childNode.id, nodes);
      result.push(...sortedChildren);
    }

    return result;
  }
}
